import { OfficeSelect } from '@cmp-src/components/selects/OfficeSelect/OfficeSelect';
import { DayDate } from '@cmp-src/data-display/dates/DayDate';
import {
  ISchedule,
  IWorkingLocation,
  IWorkingLocationPresenceType,
  WL_PRESENCE_OOO,
  WL_PRESENCE_OFFICE,
  WL_PRESENCE_REMOTE,
  AgendaSchedule,
  parseDateOnlyString
} from '@gettactic/api';
import { ReservationCard } from '@gettactic/components/src/components/reservation-card/ReservationCard';
import { useAuthenticated, useDivisionsMenuQuery } from '@gettactic/hooks';
import { CheckInCardOnWeb } from './CheckInCardOnWeb';
import { cn } from '@v2/lib/utils';
import { parseApiDate } from '@gettactic/helpers';
import { format, isPast, isToday } from 'date-fns';
import { useReserveModal } from '@/hooks/useReserveModal';
import { AgendaDay as AgendaDayType } from '@gettactic/api';
import { useRouter } from 'next/router';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '@v2/components/ui/tooltip';

interface OptimisticLocation extends Partial<IWorkingLocation> {
  location: IWorkingLocationPresenceType;
  office_id?: string;
}

export const AgendaDay = ({
  office_id,
  date,
  schedule,
  onSelectItemChange,
  selectedEvent,
  onLocationChange,
  workingLocationId,
  day,
  onReservationClick,
  location,
  isLocationLoading = false,
  optimisticLocation = null
}: {
  office_id: string | null;
  date: string;
  schedule: AgendaSchedule[] | null;
  selectedEvent: AgendaSchedule | null;
  onSelectItemChange: (schedule: AgendaSchedule | null) => void;
  onLocationChange: (location: IWorkingLocationPresenceType) => void;
  workingLocationId: string | undefined;
  day: AgendaDayType | null;
  onReservationClick: () => void;
  location: IWorkingLocationPresenceType | IWorkingLocation | null;
  isLocationLoading?: boolean;
  optimisticLocation?: AgendaDayType | null;
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  // const { data: offices } = useOfficesQuery();
  const divisions = useDivisionsMenuQuery();
  const offices = divisions.data?.tree.children;
  const topLevelOffices = divisions.data?.tree.offices;

  const { openEditModal, openCancelModal } = useReserveModal();

  const handleOpenEditModal = (schedule: ISchedule) => {
    openEditModal(schedule, true);
  };

  const handleOpenCancelModal = (schedule: ISchedule) => {
    openCancelModal([schedule]);
  };

  const effectiveDay =
    optimisticLocation ||
    (day?.location === WL_PRESENCE_OFFICE ? office_id : day);

  const effectiveLocation =
    optimisticLocation?.location ||
    (typeof location === 'string' ? location : location?.location);

  const effectiveWorkingLocationId =
    (optimisticLocation as OptimisticLocation)?.office_id || workingLocationId;

  // usually the same office for all the reservations
  // TBD: group schedule items by office later
  const defaultOffice = office_id;

  const isOOO = schedule?.some((item) => {
    // @ts-ignore - Expected error due to missing property
    return item.location === WL_PRESENCE_OOO;
  });

  // when ooo or remote assigned after the office is selected,
  // office is not being removed from the schedule
  // so we need to hide the office card
  const hideOfficeCard =
    effectiveLocation === WL_PRESENCE_REMOTE ||
    effectiveLocation === WL_PRESENCE_OOO;

  const fallbackOfficeSelectValue = defaultOffice ?? '';

  // Calculate the defaultValue for OfficeSelect
  let selectValue = '';
  // Handle all three types of locations:
  // 1. If it's remote or OOO, use the location value directly
  if (
    effectiveLocation === WL_PRESENCE_REMOTE ||
    effectiveLocation === WL_PRESENCE_OOO
  ) {
    selectValue = effectiveLocation;
  }
  // 2. If it's office, use the office ID
  else if (
    effectiveLocation === WL_PRESENCE_OFFICE &&
    (effectiveWorkingLocationId || office_id)
  ) {
    selectValue = office_id ?? effectiveWorkingLocationId ?? '';
  }
  // 3. Otherwise, use fallback
  else if (effectiveWorkingLocationId) {
    selectValue = effectiveWorkingLocationId;
  } else {
    selectValue = fallbackOfficeSelectValue;
  }
  const isPastDate = isPast(parseApiDate(date)) && !isToday(parseApiDate(date));

  const policy = day?.policy;

  return (
    <div className={cn('flex flex-col', { 'opacity-70': isLocationLoading })}>
      <div className="flex items-center justify-between space-x-2 px-1 text-sm">
        <DayDate apiDate={date} />
        <div>
          <div className="flex items-center gap-1">
            {/* If policy is requested, don't allow user to change location */}
            {offices && !policy ? (
              <OfficeSelect
                scrollViewportClassname="px-0"
                showRemote={true}
                showOOO={true}
                defaultValue={selectValue}
                divisions={divisions.data}
                onValueChange={async (value) => {
                  await onLocationChange(value as IWorkingLocationPresenceType);
                }}
                disabled={isPastDate || !!policy}
              />
            ) : (
              <div className="h-3 w-1 bg-transparent" />
            )}
            {policy && (
              <PolicyButton
                policy={policy}
                status={effectiveLocation}
                date={date}
              />
            )}
          </div>
        </div>
      </div>
      <div className={cn('flex flex-col gap-1')}>
        {!policy && schedule && schedule.length > 0 ? (
          schedule?.map((item) => {
            // const canEdit = getCurrentUserCanEdit(item, authenticatedUser);
            const isSelected = selectedEvent?.id === item.id;
            return (
              <CheckInCardOnWeb
                key={item.id}
                reservation={item}
                onClick={() => {
                  if (isSelected) {
                    onSelectItemChange(null);
                    return;
                  }
                  onSelectItemChange(item);
                }}
                layoutProps={{
                  isSelected
                }}
                onEdit={async () => {
                  await handleOpenEditModal(item);
                }}
                onCancel={async () => {
                  await handleOpenCancelModal(item);
                }}
              />
            );
          })
        ) : (
          <ReservationCard
            isPastDate={isPastDate}
            office={
              office_id ? authenticatedUser.offices?.byId[office_id] : undefined
            }
            isInOffice={
              !hideOfficeCard &&
              effectiveDay &&
              typeof effectiveDay === 'object' &&
              'office' in effectiveDay
                ? !!effectiveDay.office
                : day?.location === WL_PRESENCE_OFFICE
            }
            onReservationClick={onReservationClick}
            disabled={isPastDate || !!policy}
          />
        )}
      </div>
    </div>
  );
};

const PolicyButton = ({
  policy,
  status,
  date
}: {
  policy: AgendaDayType['policy'];
  date: string;
  status: IWorkingLocationPresenceType | undefined;
}) => {
  const statusName =
    status === WL_PRESENCE_REMOTE
      ? 'Remote'
      : status === WL_PRESENCE_OOO
        ? 'Out of Office'
        : null;
  const router = useRouter();
  if (!policy) {
    return null;
  }
  return (
    <TooltipProvider delayDuration={0}> 
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className='cursor-pointer py-3'
          onClick={() =>
            router.push({
              pathname: '/settings/attendance',
              query: {
                start: format(parseDateOnlyString(date), 'yyyy-MM-dd')
              }
            })
          }
        >
          {statusName} ({policy.type.charAt(0).toUpperCase() + policy.type.slice(1)} Policy)  
        </button>
      </TooltipTrigger>
      <TooltipContent>
        My Attendance Requests
      </TooltipContent>
    </Tooltip>
    </TooltipProvider>
  );
};
